import { useMutationState } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useCaseNotifications } from '@/lib/notifications/use-notifications';
import type { ISendNotificationBody, ISendNotificationResult } from '@/lib/notifications/types';
import { CHANNEL_LABEL_KEY } from '@/lib/notifications/labels';
import { passengerSeen } from '@/lib/notifications/engagement';
import { cn } from '@/lib/cn';
import { toneClasses } from './airport-tokens';
import { notificationFailureReason } from './pnr-steps';

/** Delivery evidence stays inside the drawer, visible on every tab. */
export function PnrNotificationStatus({
  caseUrn,
  subCaseUrn,
  inline = false,
  answered = false,
  transientOnly = false,
}: {
  caseUrn: string;
  subCaseUrn: string;
  /** Inside a card (the passenger card's conversation): no outer margins. */
  inline?: boolean;
  /** The passenger already answered this offer (the card's own read of the row). */
  answered?: boolean;
  /** Only while a send is on its way or failed to leave, or the status cannot be read — the
   *  conversation's own lines already say the rest. */
  transientOnly?: boolean;
}) {
  const { t, i18n } = useTranslation('pnr');
  const { rowFor, isLoading, isError, deliveryStatusAvailable, refetch } = useCaseNotifications(caseUrn);
  const row = rowFor(subCaseUrn);
  // The footer, next-action button and contact dialog all share this request history.
  // Match the notification ID so an older failed attempt cannot overwrite a fresh send.
  const requests = useMutationState({
    filters: { mutationKey: ['notification-send', caseUrn] },
    select: (mutation) => ({
      body: mutation.state.variables as ISendNotificationBody | undefined,
      result: mutation.state.data as ISendNotificationResult | undefined,
      status: mutation.state.status,
      submittedAt: mutation.state.submittedAt,
    }),
  });
  const request = requests
    .filter((r) => r.body?.subCaseUrn === subCaseUrn)
    .sort((a, b) => b.submittedAt - a.submittedAt)[0];
  const newerDelivery = !!row?.lastEventAt && !!request && Date.parse(row.lastEventAt) > request.submittedAt;
  const requestFailed = !newerDelivery && (request?.status === 'error' ||
    (request?.status === 'success' && request.result?.status !== 'queued'));
  const newQueued = request?.result?.status === 'queued' &&
    !!request.result.notificationUrns?.length &&
    !request.result.notificationUrns.includes(row?.notificationUrn ?? '') &&
    (!row?.lastEventAt || Date.parse(row.lastEventAt) <= request.submittedAt);
  const preparing = request?.status === 'pending';
  const channel = preparing || newQueued ? request?.body?.channel : row?.channel;
  const unavailable = isError || deliveryStatusAvailable === false;
  const state = newQueued ? 'pending' : row?.deliveryState ?? 'unknown';
  const failed = requestFailed || (!preparing && !newQueued && state === 'failed');
  // What the passenger did with it — saw the offer, got into the app, opened the
  // link: the strongest delivery evidence there is.
  const seen = !failed && !preparing && !newQueued && !unavailable ? passengerSeen(row) : null;
  const opened = !!seen;
  const openedAt = seen
    ? new Date(seen.at).toLocaleString(i18n.resolvedLanguage ?? i18n.language, {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    })
    : undefined;
  // Once the offer is answered, «they must accept or decline» is no longer true.
  const replied =
    row?.acceptanceState === 'accepted' || row?.acceptanceState === 'declined' || (answered && row?.acceptanceState !== 'awaiting');
  const action = replied ? 'NONE' : (row?.requestedAction ?? (row?.acceptanceState === 'awaiting' ? 'ACCEPT_OFFER' : 'NONE'));
  const reason = notificationFailureReason(row?.failureReason);
  if (transientOnly && !preparing && !requestFailed && !newQueued && !unavailable) return null;

  return (
    <div
      className={cn('flex shrink-0 flex-wrap items-center gap-x-2 gap-y-1 rounded-md border px-3 py-2 text-xs',
        inline ? 'text-[13px]' : 'mx-5 mb-2 sm:mx-6',
        failed ? 'border-destructive/30 bg-destructive/5 text-destructive'
          : opened ? cn(toneClasses('ok').border, toneClasses('ok').surface, toneClasses('ok').text)
          : 'border-border bg-muted/30 text-muted-foreground')}
      role="status"
      aria-live="polite"
      data-testid="pnr-notification-status"
      data-state={failed ? 'failed' : opened ? 'opened' : state}
    >
      <span className="font-semibold">{t('notification.current.title')}</span>
      <span>
        {preparing ? t('notification.current.preparing')
          : requestFailed ? t('notification.current.requestFailed')
          : unavailable ? t('notification.statusUnavailable')
          : isLoading ? t('offer.loading')
          : seen ? t(`notification.current.${seen.kind}`, { when: openedAt })
          : t(`notification.current.${state}`)}
      </span>
      {!preparing && !requestFailed && !unavailable && channel ? (
        <span>· {t(CHANNEL_LABEL_KEY[channel])}</span>
      ) : null}
      {failed && !requestFailed && !unavailable ? <span>{t(`notification.current.failure.${reason}`)}</span> : null}
      {!preparing && !newQueued && !requestFailed && !unavailable && !isLoading && !replied ? (
        <span>· {t(action === 'CONTACT_DESK' ? 'notification.current.contactDesk'
          : action === 'ACCEPT_OFFER'
            ? 'notification.current.actionRequired' : 'notification.current.noActionRequired')}</span>
      ) : null}
      {unavailable ? (
        <button type="button" className="underline" onClick={refetch}>{t('notification.retry')}</button>
      ) : null}
    </div>
  );
}
